import type { User } from '@/types';

/** Role codes that grant full platform access (aligned with backend auth-user.util). */
const SUPER_ADMIN_ROLE_CODES = new Set([
  'super-admin',
  'super_admin',
  'superadmin',
  'platform_super_admin',
]);

function normalizeRoleCode(code: string): string {
  return code.trim().toLowerCase().replace(/_/g, '-');
}

export function isSuperAdminRoleCode(code: string | null | undefined): boolean {
  if (!code || typeof code !== 'string') return false;
  return SUPER_ADMIN_ROLE_CODES.has(normalizeRoleCode(code));
}

export function isSuperAdminRoleName(name: string | null | undefined): boolean {
  if (!name || typeof name !== 'string') return false;
  const n = name.trim().toLowerCase();
  return n === 'super admin' || n === 'superadmin';
}

/** Normalize platform super-admin flag from DB, JWT, or API (0/1/true). */
export function parseIsSuperAdminFlag(value: unknown): boolean {
  return (
    value === true ||
    value === 1 ||
    value === '1' ||
    (typeof value === 'string' && value.toLowerCase() === 'true')
  );
}

/**
 * Full module/menu access: users.is_super_admin and/or super-admin role code on JWT.
 */
export function isPlatformSuperAdmin(
  user: Pick<User, 'is_super_admin' | 'roles'> | null | undefined
): boolean {
  if (!user) return false;
  if (parseIsSuperAdminFlag(user.is_super_admin)) return true;
  const roles = user.roles;
  if (!Array.isArray(roles)) return false;
  return roles.some((code) => isSuperAdminRoleCode(code));
}
